import { useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Notification, required, useNotify, useRedirect } from 'react-admin';
import CommonTextInput from '../components/inputs/CommonTextInput';
import CommonPasswordInput from '../components/inputs/CommonPasswordInput';
import authSx from '../shared/styles/authSx';
import { LLButton } from '@/components-library';
import useLogin from '../shared/hooks/auth/useLogin';
import { LLLink } from '@/components-library';
import { useUrlParams } from '../shared/hooks/useUrlParams';
import { useForm, FormProvider } from 'react-hook-form';
import OnlyWithNoAuth from './OnlyWithNoAuth';
import SwishLogo from '../shared/icons/SwishLogo';
import { signInWithPopup, GoogleAuthProvider, OAuthProvider } from 'firebase/auth';
import { firebaseAuth } from '@/src/auth/FirebaseAuth';
import { Resources } from '@/src/shared/constants/resources';
import AppleIcon from '@mui/icons-material/Apple';

type SubmitProps = { email: string; password: string };

const LoginPage = () => {
  const notify = useNotify();
  const { login, isLoading } = useLogin();
  const params = useUrlParams();
  const redirect = useRedirect();
  const form = useForm<SubmitProps>({ mode: 'onBlur' });

  function onSubmit(e: SubmitProps) {
    const { email, password } = e;
    login({ email, password }).catch((error: Error) => {
      notify(error.message, {
        type: 'error',
      });
    });
  }

  useEffect(() => {
    const error = params.get('error');
    if (error) {
      notify(error, {
        type: 'error',
      });
      redirect('/login');
    }
  }, []);

  const isAppleDevice = /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);

  async function submitSso(provider: GoogleAuthProvider | OAuthProvider) {
    try {
      await signInWithPopup(firebaseAuth, provider);
      redirect(`/${Resources.ClubProfile}`);
    } catch (error) {
      console.error('Login Error:', error);
    }
  }

  return (
    <OnlyWithNoAuth>
      <Notification />
      <Box sx={authSx.root} display="flex" justifyContent="center" alignItems="center">
        <Box py={{ xs: 3, sm: 4 }} width="100%" maxWidth={505} px={{ xs: 2, sm: 0 }}>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <Box
                sx={[
                  authSx.form,
                  { pt: { xs: 4, sm: 5 }, pb: { xs: 6, sm: 10 }, px: { xs: 3, sm: 8 } },
                ]}
              >
                <Box mb={2} textAlign={'center'}>
                  <SwishLogo text="bottom" scale={20} />
                </Box>
                <Typography
                  variant="body1"
                  sx={{ display: 'inline-block', mb: 1, textAlign: 'center' }}
                >
                  Enter your club information to get started.
                  <br />
                  The best league and tournament software awaits!
                </Typography>
                <CommonTextInput
                  sx={authSx.input}
                  source="email"
                  validate={[required()]}
                  placeholder={'Email address'}
                  type={'email'}
                  autoComplete="email"
                  fullWidth
                />
                <Box sx={authSx.input}>
                  <CommonPasswordInput
                    source="password"
                    validate={[required()]}
                    placeholder={'Password'}
                    autoComplete="current-password"
                    fullWidth
                  />
                  <Box sx={{ mb: 2, mt: -3 }}>
                    <LLLink to="/password-recovery">
                      <Typography variant={'caption'}>Forgot password?</Typography>
                    </LLLink>
                  </Box>
                  <Typography variant="caption" color="grey.600" sx={{ mb: 2 }}>
                    *Login credentials are the same as your Swish app admin credentials.
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography component={'span'} variant={'caption'}>
                      Don’t have an account?
                    </Typography>
                    &nbsp;
                    <LLLink to="/signup">
                      <Typography variant={'caption'}>Sign up</Typography>
                    </LLLink>
                  </Box>
                </Box>
                <LLButton isLoading={isLoading} type={'submit'} size={'large'} fullWidth>
                  Continue
                </LLButton>
                <Box textAlign="center" mt={2}>
                  or
                </Box>
                <Box textAlign="center" mt={2} display="flex" flexDirection="column" gap={1.5}>
                  <Button
                    onClick={() => submitSso(new GoogleAuthProvider())}
                    type="button"
                    sx={authSx.googleLoginBtn}
                    variant="outlined"
                    color="inherit"
                    startIcon={
                      <img
                        width="20px"
                        alt="Google sign-in"
                        src="/img/Google__G__Logo.svg.webp"
                      />
                    }
                  >
                    Continue with Google
                  </Button>
                  {isAppleDevice && (
                    <Button
                      onClick={() => {
                        const appleProvider = new OAuthProvider('apple.com');
                        appleProvider.addScope('email');
                        appleProvider.addScope('name');
                        submitSso(appleProvider);
                      }}
                      type="button"
                      sx={authSx.appleLoginBtn}
                      variant="outlined"
                      color="inherit"
                      startIcon={<AppleIcon />}
                    >
                      Continue with Apple
                    </Button>
                  )}
                </Box>
              </Box>
            </form>
          </FormProvider>
        </Box>
      </Box>
    </OnlyWithNoAuth>
  );
};

export default LoginPage;
